import PropTypes from "prop-types";
import { PullToRefreshify } from "react-pull-to-refreshify";
import { usePullRefresh } from "./usePullRefresh";
import "./pullRefresh.css";

const isTouchCapable =
  typeof window !== "undefined" &&
  ("ontouchstart" in window || navigator.maxTouchPoints > 0);

const prefersReducedMotion =
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;

const STATUS_LABEL = {
  pulling: "Pull to refresh",
  canRelease: "Release to refresh",
  refreshing: "Refreshing…",
  complete: "Updated",
};

const renderIndicator = (status, _percent) => {
  if (status === "normal" || !status) return null;

  const label = STATUS_LABEL[status];

  return (
    <div
      role="status"
      aria-live="polite"
      className="ptr-indicator"
      style={{ color: status === "complete" ? "#34C759" : "#007AFF" }}
    >
      {status === "refreshing" ? (
        <span className="ptr-spinner" aria-hidden="true" />
      ) : status === "complete" ? (
        <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
          <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.5.5 0 0 0-1.06 1.06L6.97 11.03a.5.5 0 0 0 1.079-.02l3.992-4.99a.5.5 0 0 0-.01-1.05z" />
        </svg>
      ) : (
        <svg
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          fill="currentColor"
          viewBox="0 0 16 16"
          className={`ptr-arrow${status === "canRelease" ? " ptr-arrow-flip" : ""}`}
        >
          <path fillRule="evenodd" d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 1 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z" />
        </svg>
      )}
      <span className="ptr-indicator-label">{label}</span>
    </div>
  );
};

/**
 * Drop-in pull-to-refresh for any page that relies on document/body scroll.
 * Wrap a page's root element and pass the page's own data-loading function
 * as `onRefresh` -- no fetch logic is duplicated here.
 *
 * <PullToRefreshWrapper onRefresh={loadProducts}>
 *   <Container>...</Container>
 * </PullToRefreshWrapper>
 *
 * Disabled automatically on devices without touch support, so desktop
 * browsers render and behave exactly as before.
 */
export const PullToRefreshWrapper = ({
  onRefresh,
  children,
  disabled = false,
  className,
  style,
  ...rest
}) => {
  const { refreshing, handleRefresh } = usePullRefresh(onRefresh);

  return (
    <PullToRefreshify
      refreshing={refreshing}
      onRefresh={handleRefresh}
      renderText={renderIndicator}
      disabled={disabled || !isTouchCapable}
      animationDuration={prefersReducedMotion ? 0 : 300}
      className={className}
      style={style}
      {...rest}
    >
      {children}
    </PullToRefreshify>
  );
};

PullToRefreshWrapper.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default PullToRefreshWrapper;
